import React, { useMemo, useState } from 'react';
import { Upload, AlertTriangle, CheckCircle2, XCircle, ArrowRight, Loader2, Link as LinkIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Input } from './ui/input';
import StaffCombobox from './StaffCombobox';
import { toast } from 'sonner';
import { staffApi } from '../lib/api';

const STATUS_TONE = {
  alias: { tone: 'bg-blue-50 text-blue-700 border-blue-200', icon: LinkIcon, label: 'ALIAS' },
  auto: { tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'AUTO' },
  review: { tone: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle, label: 'REVIEW' },
  no_match: { tone: 'bg-red-50 text-red-700 border-red-200', icon: XCircle, label: 'NO MATCH' },
};

const MONTHS = [
  { value: 1, label: 'Gennaio' }, { value: 2, label: 'Febbraio' }, { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' }, { value: 5, label: 'Maggio' }, { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Dicembre' },
];

export default function FreshaImportDialog({ staffMembers, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const today = new Date();
  // Default: mese precedente (Fresha report tipicamente è del mese chiuso)
  const defaultMonth = today.getMonth() === 0 ? 12 : today.getMonth();
  const defaultYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [preview, setPreview] = useState(null);
  // assignments: { fresha_name: staff_id (o null per skip) }
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  async function handleUpload() {
    if (!file) { toast.error('Seleziona un file CSV'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await staffApi.importFreshaPreview(fd);
      setPreview(r.data);
      // Pre-popolo assignments SOLO per alias esistenti (safety-first
      // 2026-07-01: gli status auto/review non esistono più; ogni riga senza
      // alias è `no_match` e va confermata a mano).
      const init = {};
      for (const row of r.data.rows) {
        if (row.status === 'alias') {
          init[row.fresha_name] = row.staff_id_match;
        } else {
          init[row.fresha_name] = null;
        }
      }
      setAssignments(init);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore preview');
    } finally { setLoading(false); }
  }

  function setAssignment(freshaName, staffId) {
    setAssignments(prev => ({ ...prev, [freshaName]: staffId === 'none' ? null : staffId }));
  }

  const finalCounts = useMemo(() => {
    if (!preview) return null;
    let linked = 0, skipped = 0;
    for (const r of preview.rows) {
      if (assignments[r.fresha_name]) linked++;
      else skipped++;
    }
    return { linked, skipped };
  }, [preview, assignments]);

  async function handleCommit() {
    setLoading(true);
    try {
      const rows = preview.rows.map(r => ({
        fresha_name: r.fresha_name,
        staff_id_match: assignments[r.fresha_name] || null,
        from_alias: r.from_alias && assignments[r.fresha_name] === r.staff_id_match,
        confidence: r.confidence,
        metrics: r.metrics,
      }));
      const r = await staffApi.importFreshaCommit({ rows, year, month, save_aliases: true });
      setImportResult(r.data);
      setStep(3);
      toast.success(`${r.data.imported} performance importate · ${r.data.alias_saved} alias salvati`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore import');
    } finally { setLoading(false); }
  }

  // Tab per filtrare lo step 2. `auto`/`review` sono deprecati; ora solo
  // `all` e `no_match` (`no_match_with_sugg` = subset con suggerimento).
  const [filter, setFilter] = useState('all');
  const filteredRows = useMemo(() => {
    if (!preview) return [];
    if (filter === 'all') return preview.rows;
    if (filter === 'no_match_with_sugg') {
      return preview.rows.filter(r => r.status === 'no_match' && r.suggested_staff_id);
    }
    return preview.rows.filter(r => r.status === filter);
  }, [preview, filter]);

  // Ordina staff alfabeticamente per il dropdown (2026-07-01, richiesta utente).
  const sortedStaff = useMemo(() => (
    [...(staffMembers || [])].sort((a, b) => {
      const na = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLocaleLowerCase('it');
      const nb = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLocaleLowerCase('it');
      return na.localeCompare(nb, 'it');
    })
  ), [staffMembers]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="fresha-import-dialog">
        <DialogHeader>
          <DialogTitle>Importa performance da Fresha</DialogTitle>
          <DialogDescription>
            Step {step} di 3 — {step === 1 ? 'Carica il report performance + scegli il periodo' : step === 2 ? 'Conferma il linkage Nome Fresha → Dipendente StoreOps' : 'Risultato import'}
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-4 py-2" data-testid="step-upload-fresha">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide">Anno *</Label>
                <Input type="number" min={2020} max={2099} value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="fresha-year" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide">Mese *</Label>
                <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
                  <SelectTrigger data-testid="fresha-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-sm p-8 text-center">
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <Label className="text-sm font-medium text-slate-700 cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                  data-testid="fresha-file-input"
                />
                <span className="bg-slate-900 text-white px-4 py-2 inline-block rounded-sm hover:bg-slate-700 transition-colors">
                  {file ? file.name : 'Seleziona file CSV Fresha'}
                </span>
              </Label>
              <p className="text-xs text-slate-500 mt-3">
                "report performance summary" da Fresha · max 10MB
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-600">
              <p className="font-semibold mb-1">Cosa succede:</p>
              <ul className="space-y-1 list-disc pl-4">
                <li>Estrazione 24 metriche per dipendente (vendite, servizi, ore, occupazione, clienti)</li>
                <li>Solo gli <strong>alias già confermati in passato</strong> vengono auto-importati (safety-first: nessuna auto-attribuzione di metriche)</li>
                <li>Per i nomi non ancora mappati, il sistema propone un suggerimento fuzzy che devi <strong>accettare a mano</strong> con un click</li>
                <li>Skip righe Admin/Responsabile/LIM SRL</li>
                <li>I match confermati vengono salvati come alias per gli import futuri (mensili e daily via estensione)</li>
              </ul>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && preview && (
          <div className="space-y-3 py-2" data-testid="step-linkage">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Righe nel CSV" value={preview.total_rows} />
              <Stat label="Alias riusati" value={preview.counts.alias || 0} tone="blue" />
              <Stat label="Non trovati" value={preview.counts.no_match || 0} tone="red" />
              <Stat label="Con suggerimento" value={preview.no_match_with_suggestion || 0} tone="amber" />
            </div>

            <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2">
              <div className="flex gap-1">
                {['all', 'no_match', 'no_match_with_sugg'].map(f => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`text-xs px-3 py-1.5 border rounded-sm ${filter === f ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`filter-${f}`}
                  >
                    {f === 'all'
                      ? `Tutti (${preview.rows.length})`
                      : f === 'no_match'
                        ? `Non trovati (${preview.counts.no_match || 0})`
                        : `Con suggerimento (${preview.no_match_with_suggestion || 0})`}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                Pronti per import: <strong className="text-slate-900">{finalCounts?.linked || 0}</strong> · saltati: <strong className="text-slate-900">{finalCounts?.skipped || 0}</strong>
              </p>
            </div>

            <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[55vh] overflow-y-auto">
              {filteredRows.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">Nessuna riga in questa categoria.</div>
              ) : (
                filteredRows.map(r => {
                  const cfg = STATUS_TONE[r.status] || STATUS_TONE.no_match;
                  const Icon = cfg.icon;
                  const current = assignments[r.fresha_name] || '';
                  const hasSuggestion = !!r.suggested_staff_id;
                  const suggAccepted = hasSuggestion && current === r.suggested_staff_id;
                  return (
                    <div key={r.fresha_name} className="p-2.5 flex items-center gap-2.5 flex-wrap" data-testid={`fresha-row-${r.fresha_name.replace(/\W+/g, '-')}`}>
                      <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 border rounded-sm inline-flex items-center gap-1 shrink-0 ${cfg.tone}`}>
                        <Icon className="w-3 h-3" /> {cfg.label} {r.confidence > 0 ? `${r.confidence}%` : ''}
                      </span>
                      <div className="flex-1 min-w-[150px]">
                        <p className="text-sm font-medium text-slate-900">{r.fresha_name}</p>
                        <p className="text-[11px] text-slate-500">
                          € {(r.metrics.total_sales || 0).toFixed(0)} · {r.metrics.services_sold || 0} servizi · {r.metrics.total_clients || 0} clienti
                        </p>
                        {hasSuggestion && !suggAccepted && (
                          <button
                            type="button"
                            onClick={() => setAssignment(r.fresha_name, r.suggested_staff_id)}
                            className="mt-1 text-[11px] px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1"
                            data-testid={`accept-sugg-${r.fresha_name.replace(/\W+/g, '-')}`}
                          >
                            <ArrowRight className="w-3 h-3" /> Suggerito: {r.suggested_staff_name} ({r.suggested_confidence}%)
                          </button>
                        )}
                        {suggAccepted && (
                          <span className="mt-1 text-[11px] text-emerald-700 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Suggerimento accettato
                          </span>
                        )}
                      </div>
                      <StaffCombobox
                        value={current || null}
                        onChange={(v) => setAssignment(r.fresha_name, v)}
                        staffList={sortedStaff}
                        allowNone
                        noneLabel="— Salta (no import) —"
                        placeholder="Seleziona dipendente…"
                        testId={`assign-${r.fresha_name.replace(/\W+/g, '-')}`}
                        className="w-64 h-9 text-xs"
                      />
                    </div>
                  );
                })
              )}
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-600">
              <p>📌 Periodo selezionato: <strong>{MONTHS.find(m => m.value === month)?.label} {year}</strong>. Cambialo dallo step 1 se sbagliato.</p>
              <p>💾 I match confermati verranno memorizzati: la prossima volta che importerai questo CSV, il linkage sarà automatico.</p>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && importResult && (
          <div className="space-y-4 py-4" data-testid="step-result-fresha">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
              <p className="font-bold text-lg text-emerald-900">Performance importate</p>
              <p className="text-sm text-emerald-800 mt-1">
                <strong>{importResult.imported}</strong> dipendenti aggiornati per <strong>{importResult.period}</strong>
              </p>
              <p className="text-xs text-emerald-700 mt-2">
                {importResult.alias_saved} nuovi alias salvati · {importResult.skipped} righe saltate
              </p>
            </div>
            {importResult.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <p className="text-xs font-semibold text-red-800 mb-1">Errori ({importResult.errors.length}):</p>
                <ul className="text-xs text-red-700 space-y-0.5 max-h-32 overflow-y-auto">
                  {importResult.errors.map((e, i) => (
                    <li key={`fr-err-${e.fresha_name || i}`}>{e.fresha_name}: {e.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button onClick={handleUpload} disabled={!file || loading} className="gap-1.5" data-testid="fresha-upload-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Analizza CSV
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>Indietro</Button>
              <Button onClick={handleCommit} disabled={loading || (finalCounts?.linked || 0) === 0} className="gap-1.5" data-testid="fresha-commit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Importa {finalCounts?.linked || 0} performance
              </Button>
            </>
          )}
          {step === 3 && (
            <Button onClick={() => { onImported(importResult); onClose(); }} data-testid="fresha-done-btn">
              Chiudi
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <div className={`border rounded-sm p-2.5 ${tones[tone] || 'bg-slate-50 border-slate-200 text-slate-700'}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold">{label}</p>
      <p className="text-xl font-bold mt-0.5">{value}</p>
    </div>
  );
}
